import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Loader2,
  Receipt,
  RefreshCw,
} from "lucide-react";
import { buildTimelineSections } from "../../utils/activityHelpers";
import {
  formatBalance,
  getTxDateKey,
  summarizeTxLedger,
  TX_STATUS_FILTERS,
  TX_TYPE_FILTERS,
} from "../../utils/dashboardHelpers";
import DashboardTransactionRow from "./DashboardTransactionRow";
import TransactionsAnalyticsPanel from "./TransactionsAnalyticsPanel";

const LEDGER_VIEWS = [
  { id: "analytics", label: "Analytics", Icon: BarChart3 },
  { id: "ledger", label: "Ledger", Icon: Receipt },
];

const DashboardTransactionsPanel = ({
  transactions,
  analyticsTx = [],
  analyticsLoading = false,
  loading,
  loadingMore,
  error,
  filter,
  pagination,
  onFilterChange,
  onRefresh,
  onLoadMore,
  defaultView = "analytics",
}) => {
  const [view, setView] = useState(defaultView);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setView(defaultView);
  }, [defaultView]);

  useEffect(() => {
    setExpandedId(null);
  }, [filter.type, filter.status]);

  const groupedSections = useMemo(
    () => buildTimelineSections(transactions, getTxDateKey),
    [transactions]
  );

  const summary = useMemo(() => summarizeTxLedger(transactions), [transactions]);
  const net = summary.credits - summary.debits;

  const handleTypeFilter = (value) => {
    if (value === filter.type) return;
    onFilterChange({ type: value });
  };

  const handleStatusFilter = (value) => {
    if (value === filter.status) return;
    onFilterChange({ status: value });
  };

  const resetAllFilters = () => {
    if (filter.type === "all" && filter.status === "all") return;
    onFilterChange({ type: "all", status: "all" });
  };

  const hasFilter = filter.type !== "all" || filter.status !== "all";
  const totalCount = pagination?.totalTransactions || transactions.length;

  // Unfiltered view charts the wide analytics sample; a filtered view
  // re-focuses the charts on exactly what the ledger is showing.
  const insightSource = hasFilter
    ? transactions
    : analyticsTx.length >= transactions.length
    ? analyticsTx
    : transactions;

  return (
    <section className="dash-panel ledger-panel">
      <header className="ledger-toolbar">
        <div className="ledger-toolbar-main">
          <h2>Transactions</h2>
          {view === "ledger" && (
            <span className="ledger-toolbar-meta tabular-nums">
              {transactions.length}
              {totalCount > transactions.length ? ` / ${totalCount}` : ""}
              {summary.count > 0 && (
                <>
                  {" · "}
                  <span className={net >= 0 ? "ledger-net-up" : "ledger-net-down"}>
                    Net {net >= 0 ? "+" : ""}
                    {formatBalance(net, { compact: true })}
                  </span>
                </>
              )}
            </span>
          )}
        </div>

        <nav className="ledger-view-tabs" role="tablist" aria-label="Transactions views">
          {LEDGER_VIEWS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              className={`ledger-view-tab${view === id ? " ledger-view-tab-active" : ""}`}
              onClick={() => setView(id)}
            >
              <Icon size={11} aria-hidden />
              {label}
            </button>
          ))}
        </nav>

        <button
          type="button"
          className="dash-icon-btn ledger-toolbar-refresh"
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh transactions"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </header>

      {view === "analytics" ? (
        <div className="ledger-view-panel ledger-analytics-view" role="tabpanel">
          <TransactionsAnalyticsPanel
            transactions={insightSource}
            loading={analyticsLoading}
          />
        </div>
      ) : (
        <div className="ledger-view-panel ledger-ledger-view" role="tabpanel">
          <div className="ledger-filters">
            <div className="ledger-chip-row">
              <button
                type="button"
                className={`ledger-chip${!hasFilter ? " ledger-chip-active" : ""}`}
                onClick={resetAllFilters}
              >
                All
              </button>

              {TX_TYPE_FILTERS.filter(({ value }) => value !== "all").map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`ledger-chip${filter.type === value ? " ledger-chip-active" : ""}`}
                  onClick={() => handleTypeFilter(value)}
                >
                  {label}
                </button>
              ))}

              <span className="ledger-filter-divider" aria-hidden />

              {TX_STATUS_FILTERS.filter(({ value }) => value !== "all").map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`ledger-chip${filter.status === value ? " ledger-chip-active" : ""}`}
                  onClick={() => handleStatusFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="ledger-body-wrap">
            {loading && !transactions.length && (
              <div className="ra-skeleton">
                {Array.from({ length: 8 }).map((_, i) => (
                  <span key={i} className="ra-skel-row" />
                ))}
              </div>
            )}

            {error && !transactions.length && (
              <div className="dash-empty dash-empty-error">
                <p>{String(error)}</p>
                <button type="button" className="dash-btn-ghost" onClick={onRefresh}>
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && !transactions.length && (
              <div className="ra-empty">
                <span className="ra-empty-icon">
                  <Receipt size={22} />
                </span>
                <p>No transactions match these filters.</p>
                {hasFilter && (
                  <button
                    type="button"
                    className="dash-btn-ghost"
                    onClick={() => onFilterChange({ type: "all", status: "all" })}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {transactions.length > 0 && (
              <>
                {summary.count > 0 && (
                  <div className="ledger-stream-summary">
                    <div className="ws-activity-pulse tabular-nums" aria-label="Ledger summary">
                      <span className="ws-activity-pulse-in">
                        In {formatBalance(summary.credits, { compact: true })}
                      </span>
                      <span className="ws-activity-pulse-out">
                        Out {formatBalance(summary.debits, { compact: true })}
                      </span>
                      <span className={net >= 0 ? "ws-activity-pulse-net-up" : "ws-activity-pulse-net-down"}>
                        Net {net >= 0 ? "+" : ""}
                        {formatBalance(net, { compact: true })}
                      </span>
                    </div>
                  </div>
                )}

                <div className="ledger-stream">
                  <div className="ra-stream-groups">
                    {groupedSections.map((section) => (
                      <section key={section.dateKey} className="ra-stream-day">
                        <div className="ra-stream-day-head">
                          <span>{section.title}</span>
                          <em>{section.data.length}</em>
                        </div>

                        <ul className="ra-stream-list">
                          {section.data.map((tx, idx) => {
                            const txId = tx._id || tx.transactionRef;
                            return (
                              <DashboardTransactionRow
                                key={txId}
                                tx={tx}
                                expanded={expandedId === txId}
                                isLast={idx === section.data.length - 1}
                                onToggle={() =>
                                  setExpandedId((prev) => (prev === txId ? null : txId))
                                }
                              />
                            );
                          })}
                        </ul>
                      </section>
                    ))}
                  </div>
                </div>
              </>
            )}

            {transactions.length > 0 && pagination?.hasNext && (
              <footer className="ledger-load-wrap">
                <button
                  type="button"
                  className="ws-activity-more ledger-load-more"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? <Loader2 size={13} className="animate-spin" /> : null}
                  Load more
                  {totalCount > 0 && (
                    <span className="ledger-load-count">
                      {transactions.length} / {totalCount}
                    </span>
                  )}
                </button>
              </footer>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default DashboardTransactionsPanel;
